import React, { useState, useEffect } from "react";

const News = () => {
  const [activeCategory, setActiveCategory] = useState("all");
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const categories = [
    { id: "all", name: "All News" },
    { id: "investments", name: "Investment Opportunities" },
    { id: "crops", name: "Crop Updates" },
    { id: "market", name: "Market Trends" },
    { id: "sustainability", name: "Sustainability" },
  ];

  const newsArticles = [
    {
      id: 1,
      title: "New Sustainable Farming Initiative Increases Yields by 35%",
      excerpt:
        "Our latest sustainable farming techniques have resulted in significant yield improvements while reducing environmental impact.",
      category: "sustainability",
      date: "May 15, 2023",
      readTime: "4 min read",
      image:
        "https://www.whiteclouds.com/wp-content/uploads/2024/12/Largest-Farm-Side.jpg",
      featured: true,
    },
    {
      id: 2,
      title: "Q2 Investment Returns Exceed Expectations by 18%",
      excerpt:
        "Our investors are celebrating exceptional returns this quarter, outperforming market predictions.",
      category: "investments",
      date: "May 14, 2023",
      readTime: "5 min read",
      image:
        "https://images.unsplash.com/photo-1553729459-efe14ef6055d?auto=format&fit=crop&w=1200&q=80",
    },
    {
      id: 3,
      title: "New Almond Orchard Planting Completed Ahead of Schedule",
      excerpt:
        "We've successfully planted 500 acres of almond trees, positioning us for strong returns in the coming years.",
      category: "crops",
      date: "May 13, 2023",
      readTime: "3 min read",
      image:
        "https://cdn.prod.website-files.com/5e5717f541472a2c2b58bd84/5e7165bb97c43efcb1d34d97_Blog%20Image%20-%20100x669.png",
    },
    {
      id: 4,
      title: "Global Organic Food Market Projected to Grow 14% Annually",
      excerpt:
        "Market analysis shows continued strong growth in organic food demand, benefiting our investment strategy.",
      category: "market",
      date: "May 12, 2023",
      readTime: "6 min read",
      image:
        "https://images.unsplash.com/photo-1600891964092-4316c288032e?auto=format&fit=crop&w=1200&q=80",
    },
    {
      id: 5,
      title: "Water Conservation System Saves 30 Million Gallons Annually",
      excerpt:
        "Our new irrigation technology significantly reduces water usage while maintaining crop health.",
      category: "sustainability",
      date: "May 11, 2023",
      readTime: "7 min read",
      image:
        "https://img.freepik.com/premium-photo/world-water-day-promotes-water-conservation-protect-environment-concept-environment-water-conservation-world-water-day-awareness-campaigns_864588-191539.jpg",
    },
    {
      id: 6,
      title: "New Investment Tier Opens for Sustainable Agriculture Projects",
      excerpt:
        "We're expanding opportunities for investors to participate in our eco-friendly farming initiatives.",
      category: "investments",
      date: "May 10, 2023",
      readTime: "5 min read",
      image:
        "https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&w=1200&q=80",
    },
  ];

  const filteredArticles =
    activeCategory === "all"
      ? newsArticles
      : newsArticles.filter((article) => article.category === activeCategory);

  const featuredArticle = newsArticles.find((article) => article.featured);

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-12 tracking-wide">
      {/* Header */}
      <div
        className={`text-center mb-8 transition-all duration-700 ${
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
        }`}
      >
        <h1 className="text-4xl md:text-5xl font-bold text-primaryBgColor mb-4">
          Farm Investment{" "}
          <span className="text-primaryBtnHoverColor">News</span>
        </h1>

        <p className="text-lg text-secondaryTextColor max-w-3xl mx-auto">
          Stay updated with the latest developments in sustainable agriculture
          and investment opportunities
        </p>
      </div>

      {/* Category Filters */}
      <div
        className={`flex flex-wrap justify-center gap-3 mb-12 transition-all duration-700 delay-100 ${
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
        }`}
      >
        {categories.map((category) => (
          <button
            key={category.id}
            onClick={() => setActiveCategory(category.id)}
            className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
              activeCategory === category.id
                ? "bg-primaryBtnColor text-white shadow-lg"
                : "bg-white text-primaryBtnColor hover:bg-primaryBtnHoverColor hover:text-white border border-primaryBtnColor"
            }`}
          >
            {category.name}
          </button>
        ))}
      </div>

      {/* Featured Article */}
      {featuredArticle && (
        <div
          className={`mb-8 transition-all duration-700 delay-200 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
          }`}
        >
          <div className="bg-white rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-shadow duration-300">
            <div className="lg:grid lg:grid-cols-2">
              <div className="h-64 lg:h-96 overflow-hidden">
                <img
                  src={featuredArticle.image}
                  alt={featuredArticle.title}
                  className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                />
              </div>
              <div className="p-6 lg:p-8 flex flex-col justify-center">
                <div className="flex items-center mb-4">
                  <span className="px-3 py-1 bg-primaryBtnColor text-white text-xs font-semibold rounded-full mr-3">
                    {featuredArticle.category.charAt(0).toUpperCase() +
                      featuredArticle.category.slice(1)}
                  </span>
                  <span className="text-xs text-secondaryTextColor">
                    {featuredArticle.date}
                  </span>
                </div>
                <h2 className="text-2xl lg:text-3xl font-bold text-primaryTextColor mb-4">
                  {featuredArticle.title}
                </h2>
                <p className="text-secondaryTextColor mb-6">
                  {featuredArticle.excerpt}
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-secondaryTextColor">
                    {featuredArticle.readTime}
                  </span>
                  {/* <button className="text-primaryBtnColor font-semibold flex items-center group hover:text-primaryBtnHoverColor">
                      Read more
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 ml-1 group-hover:translate-x-1 transition-transform"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button> */}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* News Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {filteredArticles
          .filter((article) => !article.featured)
          .map((article, index) => (
            <div
              key={article.id}
              className={`bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 ${
                isVisible
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-10"
              }`}
              style={{ transitionDelay: `${100 + index * 100}ms` }}
            >
              <div className="h-48 overflow-hidden">
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                />
              </div>
              <div className="p-6">
                <div className="flex items-center justify-between mb-3">
                  <span className="px-2 py-1 bg-primaryBtnColor text-white text-xs font-semibold rounded">
                    {article.category.charAt(0).toUpperCase() +
                      article.category.slice(1)}
                  </span>
                  <span className="text-xs text-secondaryTextColor">
                    {article.date}
                  </span>
                </div>
                <h3 className="text-xl font-bold text-primaryTextColor mb-3 line-clamp-2">
                  {article.title}
                </h3>
                <p className="text-secondaryTextColor mb-4 line-clamp-3">
                  {article.excerpt}
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-secondaryTextColor">
                    {article.readTime}
                  </span>
                  {/* <button className="text-primaryBtnColor text-sm font-semibold flex items-center group hover:text-primaryBtnHoverColor">
                      Read more
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button> */}
                </div>
              </div>
            </div>
          ))}
      </div>

      {/* Load More Button */}
      {/* <div
          className={`text-center mt-12 transition-all duration-700 delay-500 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
          }`}
        >
          <button className="px-8 py-3 bg-primaryBtnColor text-white font-semibold rounded-lg shadow-md hover:bg-primaryBtnHoverColor hover:shadow-lg transition-all duration-300">
            Load More News
          </button>
        </div> */}

      <style jsx>{`
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .line-clamp-3 {
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

export default News;
